import { VStack } from "@gluestack-ui/themed";

import { SelectDocumentsController } from "@repo/core/screens/PreProposal/screens/SelectDocuments/controller";
import { DocumentCard } from "@repo/core/screens/PreProposal/screens/SelectDocuments/DocumentCard";
import { ActionSheet } from "@repo/core/components/layout/Actionsheet";
import { Text } from "@repo/core/components/core/Text";
import { documentActionOptions } from "@repo/core/screens/PreProposal/screens/SelectDocuments/utils";
import { ProposalOptionsItem } from "@repo/core/screens/Proposals/screens/ProposalsSearch/components/ProposalOptionsSheet/ProposalOptionsItem";
import { RequiredDocumentTypeEnum } from "@repo/core/enums/proposals";
import { PageScaffold } from "@repo/core/components/layout/Scaffolds/PageScaffold";
import { ScaffoldFooter } from "@repo/core/components/layout/Scaffolds/components/ScaffoldFooter";
import { FixedLoading } from "@repo/core/components/core/FixedLoading";

export const SelectDocuments = () => {
  const {
    handleGoBack,
    requiredDocuments,
    actionSheetRef,
    handleOnPressOption,
    handleSelectDocument,
    handleContinue,
    isLoading,
    allPendingDocumentsUploaded,
    isUploading,
    onClickCancel,
    hasBiometryDocuments,
    isRestartRequired,
    isUnicoUnavailable,
    forceManualCapture
  } = SelectDocumentsController();

  // Count only documents that are actually pending (not uploaded)
  const pendingCount =
    requiredDocuments?.filter((doc) => doc.isUploaded !== true).length || 0;

  // Check if any document is being uploaded
  const hasAnyUploading = Object.values(isUploading).some(
    (uploading) => uploading
  );

  const primaryText = isRestartRequired ? "Reiniciar fluxo" : "Continuar";
  const pendingBannerTitle = isRestartRequired
    ? "Reinício obrigatório"
    : pendingCount === 1
      ? "1 documento pendente"
      : `${pendingCount} documentos pendentes`;
  const pendingBannerDescription = isRestartRequired
    ? "Não foi possível reaproveitar a captura anterior. Será necessário reiniciar a captura."
    : isUnicoUnavailable
      ? "A plataforma de captura está indisponível no momento. Continue com o envio manual."
      : forceManualCapture
        ? "Alguns documentos obrigatórios precisam ser enviados manualmente para continuar."
        : "Envie todos os documentos para continuar";

  const allMandatoryManualDocsSent =
    !isRestartRequired &&
    !isUnicoUnavailable &&
    (requiredDocuments?.length ?? 0) > 0 &&
    pendingCount === 0;

  return (
    <>
      {hasAnyUploading && <FixedLoading />}
      <PageScaffold
        onBackPress={!hasBiometryDocuments() ? handleGoBack : undefined}
        onPressRightIcon={handleGoBack}
        rightWebLabel="Fechar"
        title="Documentos"
        footerContent={
          <ScaffoldFooter
            primaryText={primaryText}
            primaryAction="primary"
            primaryIsLoading={isLoading}
            primaryIsDisabled={!allPendingDocumentsUploaded || isLoading}
            primaryOnPress={handleContinue}
            secondaryIsDisabled={isLoading}
            secondaryText="Cancelar"
            secondaryOnPress={onClickCancel}
          />
        }
      >
        <VStack w="$full" gap="$4">
          {(pendingCount > 0 ||
            isRestartRequired ||
            isUnicoUnavailable ||
            allMandatoryManualDocsSent) && (
            <VStack
              bg={
                isRestartRequired
                  ? "$error50"
                  : allMandatoryManualDocsSent
                    ? "$success50"
                    : "$primary50"
              }
              borderRadius="$lg"
              p="$4"
              borderWidth={1}
              borderColor={
                isRestartRequired
                  ? "$error200"
                  : allMandatoryManualDocsSent
                    ? "$success200"
                    : "$primary200"
              }
            >
              <Text
                fontSize="$sm"
                color={
                  isRestartRequired
                    ? "$error700"
                    : allMandatoryManualDocsSent
                      ? "$success800"
                      : "$primary700"
                }
                fontWeight={500}
              >
                {allMandatoryManualDocsSent
                  ? "Documentos obrigatórios anexados"
                  : pendingBannerTitle}
              </Text>
              <Text
                fontSize="$xs"
                color={
                  isRestartRequired
                    ? "$error600"
                    : allMandatoryManualDocsSent
                      ? "$success700"
                      : "$primary600"
                }
                mt="$1"
              >
                {allMandatoryManualDocsSent
                  ? [
                      "Revise ou substitua algum arquivo, se necessário, e toque em ",
                      <Text key="continue" fontWeight={700} fontSize="$xs">
                        Continuar
                      </Text>,
                      " para seguir."
                    ]
                  : pendingBannerDescription}
              </Text>
            </VStack>
          )}

          <VStack gap="$2">
            {requiredDocuments && requiredDocuments.length > 0 ? (
              requiredDocuments.map((document, index) => {
                const isUploaded = document.isUploaded === true;
                return (
                  <DocumentCard
                    key={`${document.strTipoDocumento}-${index}`}
                    title={document.strDescricao}
                    hasSelectedFile={isUploaded}
                    isUploaded={isUploaded}
                    onPress={
                      isRestartRequired
                        ? undefined
                        : () =>
                            handleSelectDocument(
                              document.strTipoDocumento as RequiredDocumentTypeEnum
                            )
                    }
                  />
                );
              })
            ) : (
              <VStack
                alignItems="center"
                justifyContent="center"
                py="$8"
                px="$4"
              >
                <Text fontSize="$md" color="$textLight500" textAlign="center">
                  {isRestartRequired
                    ? "Reinicie o fluxo para realizar uma nova captura."
                    : "Nenhum documento obrigatório foi configurado para envio neste fluxo."}
                </Text>
              </VStack>
            )}
          </VStack>
        </VStack>
      </PageScaffold>

      <ActionSheet ref={actionSheetRef}>
        <VStack w="$full" p="$2" mb="$2">
          <VStack mb="$4" px="$3">
            <Text fontSize="$md" fontWeight={500} color="$textLight600">
              Opções
            </Text>
          </VStack>

          <VStack>
            {documentActionOptions?.map((option) => (
              <ProposalOptionsItem
                key={option.label}
                {...option}
                onPress={handleOnPressOption}
              />
            ))}
          </VStack>
        </VStack>
      </ActionSheet>
    </>
  );
};
